// ./hooks/useAuditLog.js

import { useContext, useCallback } from 'react';
import { GlobalContext } from '../context/provider';
import auditLogAPI from '../api/auditLogAPI';

const useAuditLog = () => {
  const { 
    auditLogState,
    auditLogDispatch
  } = useContext(GlobalContext);
  
  // ============================================================================
  // CREATE / ADD LOGS
  // ============================================================================

  const addLog = useCallback(async (logData) => {
    auditLogDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await auditLogAPI.addLog(logData);
      if (response.success) {
        auditLogDispatch({ type: 'ADD_LOG', payload: response.data });
      }
      return response;
    } catch (error) {
      auditLogDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [auditLogDispatch]);

  // ============================================================================
  // READ / GET LOGS
  // ============================================================================

  const getStudentLogs = useCallback(async (studentId, params = {}) => {
    auditLogDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await auditLogAPI.getStudentLogs(studentId, params);
      if (response.success) {
        auditLogDispatch({ type: 'SET_STUDENT_LOGS', payload: response.data });
      }
      return response;
    } catch (error) {
      auditLogDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [auditLogDispatch]);

  const getEntityLogs = useCallback(async (entityType, entityId, params = {}) => {
    auditLogDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await auditLogAPI.getEntityLogs(entityType, entityId, params);
      if (response.success) {
        auditLogDispatch({ type: 'SET_ENTITY_LOGS', payload: response.data });
      }
      return response;
    } catch (error) {
      auditLogDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [auditLogDispatch]);

  const getUserActivity = useCallback(async (userId, params = {}) => {
    auditLogDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await auditLogAPI.getUserActivity(userId, params);
      if (response.success) {
        auditLogDispatch({ type: 'SET_USER_ACTIVITY', payload: response.data });
      }
      return response;
    } catch (error) {
      auditLogDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [auditLogDispatch]);

  const getLogsByAction = useCallback(async (action, params = {}) => {
    auditLogDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await auditLogAPI.getLogsByAction(action, params);
      if (response.success) {
        auditLogDispatch({ type: 'SET_LOGS_BY_ACTION', payload: response.data });
      }
      return response;
    } catch (error) {
      auditLogDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [auditLogDispatch]);

  // ============================================================================
  // SECURITY & MONITORING
  // ============================================================================

  const getSecurityEvents = useCallback(async (params = {}) => {
    auditLogDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await auditLogAPI.getSecurityEvents(params);
      if (response.success) {
        auditLogDispatch({ type: 'SET_SECURITY_EVENTS', payload: response.data });
      }
      return response;
    } catch (error) {
      auditLogDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [auditLogDispatch]);

  const getFailedLoginAttempts = useCallback(async (params = {}) => {
    auditLogDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await auditLogAPI.getFailedLoginAttempts(params);
      if (response.success) {
        auditLogDispatch({ type: 'SET_FAILED_LOGINS', payload: response.data });
      }
      return response;
    } catch (error) {
      auditLogDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [auditLogDispatch]);

  const getCriticalEvents = useCallback(async (params = {}) => {
    auditLogDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await auditLogAPI.getCriticalEvents(params);
      if (response.success) {
        auditLogDispatch({ type: 'SET_CRITICAL_EVENTS', payload: response.data });
      }
      return response;
    } catch (error) {
      auditLogDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [auditLogDispatch]);

  // ============================================================================
  // REPORTING & ANALYTICS
  // ============================================================================

  const getComplianceReport = useCallback(async (params) => {
    auditLogDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await auditLogAPI.getComplianceReport(params);
      if (response.success) {
        auditLogDispatch({ type: 'SET_COMPLIANCE_REPORT', payload: response.data });
      }
      return response;
    } catch (error) {
      auditLogDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [auditLogDispatch]);

  const getSystemActivity = useCallback(async (params) => {
    auditLogDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await auditLogAPI.getSystemActivity(params);
      if (response.success) {
        auditLogDispatch({ type: 'SET_SYSTEM_ACTIVITY', payload: response.data });
      }
      return response;
    } catch (error) {
      auditLogDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [auditLogDispatch]);

  const getStatistics = useCallback(async (params = {}) => {
    auditLogDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await auditLogAPI.getStatistics(params);
      if (response.success) {
        auditLogDispatch({ type: 'SET_STATISTICS', payload: response.data });
      }
      return response;
    } catch (error) {
      auditLogDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [auditLogDispatch]);

  const searchLogs = useCallback(async (params) => {
    auditLogDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await auditLogAPI.searchLogs(params);
      if (response.success) {
        auditLogDispatch({ type: 'SET_SEARCH_RESULTS', payload: response.data });
      }
      return response;
    } catch (error) {
      auditLogDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [auditLogDispatch]);

  // ============================================================================
  // MAINTENANCE
  // ============================================================================

  const cleanupOldLogs = useCallback(async (cleanupData) => {
    auditLogDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await auditLogAPI.cleanupOldLogs(cleanupData);
      return response;
    } catch (error) {
      auditLogDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [auditLogDispatch]);

  // ============================================================================
  // UTILITY
  // ============================================================================

  const clearAuditLogData = useCallback(() => {
    auditLogDispatch({ type: 'CLEAR_AUDIT_LOG_DATA' });
  }, [auditLogDispatch]);

  const exportActivities = useCallback(async (loginId) => {
    auditLogDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await auditLogAPI.exportActivities(loginId);
      return response;
    } catch (error) {
      auditLogDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [auditLogDispatch]);

  const getActionsByRole = useCallback(async (role) => {
    try {
      const response = await auditLogAPI.getActionsByRole(role);
      return response;
    } catch (error) {
      auditLogDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [auditLogDispatch]);

  return {
    // auditLogState
    studentLogs: auditLogState.studentLogs,
    entityLogs: auditLogState.entityLogs,
    userActivity: auditLogState.userActivity,
    logsByAction: auditLogState.logsByAction,
    securityEvents: auditLogState.securityEvents,
    failedLogins: auditLogState.failedLogins,
    criticalEvents: auditLogState.criticalEvents,
    complianceReport: auditLogState.complianceReport,
    systemActivity: auditLogState.systemActivity,
    statistics: auditLogState.statistics,
    searchResults: auditLogState.searchResults,
    isLoading: auditLogState.isLoading,
    error: auditLogState.error,

    // Methods
    addLog,
    getStudentLogs,
    getEntityLogs,
    getUserActivity,
    getLogsByAction,
    getSecurityEvents,
    getFailedLoginAttempts,
    getCriticalEvents,
    getComplianceReport,
    getSystemActivity,
    getStatistics,
    searchLogs,
    cleanupOldLogs,
    clearAuditLogData,
    exportActivities,
    getActionsByRole
  };
};

export default useAuditLog;